/* «Esto ya está cargado» — antes de crearlo, no después.
 *
 * ── QUÉ SE CHEQUEABA Y QUÉ NO ─────────────────────────────────────────────
 *
 * El alta ya miraba si el ID existía (`obtenerDocumento`), y eso es lo que
 * evita pisar una fila. Pero el ID de tractores y furgones es el INTERNO, así
 * que **el dominio no lo miraba nadie**: la misma patente se podía cargar dos
 * veces con dos internos distintos y no fallaba nada. Después aparece en un
 * reporte por patente contada dos veces, y para entonces ya no se sabe cuál de
 * las dos filas es la buena.
 *
 * ── BLOQUEAR Y AVISAR NO SON LO MISMO ─────────────────────────────────────
 *
 * Y la diferencia es la que ordena todo este módulo:
 *
 *   · CHOCA CON EL ID → se bloquea. Guardar ahí PISA una fila que ya existe;
 *     no es un aviso, es una pérdida de datos.
 *   · CHOCA CON OTRO CAMPO → se avisa, con la fila que ya lo tiene, y decide
 *     la persona. Una patente repetida casi siempre es un error, pero también
 *     es lo que pasa el día que una chapa se pasa de una unidad a otra —y el
 *     que está cargando sabe cuál de las dos cosas es, y el sistema no.
 *
 * Es la misma regla que `mecanicos.parecidos()` y que el cotejo del chofer:
 * la discrepancia se MUESTRA, no se resuelve sola.
 *
 * Y es puro: entran las filas que ya hay y lo que se quiere guardar, sale la
 * lista de choques. Se prueba en una tabla.
 */

/* Qué no puede estar repetido, por colección.
 *
 * `id` no está en la lista: ése lo chequea el alta aparte y bloquea, que es
 * otra cosa. Acá van los campos que identifican a la unidad EN EL MUNDO —la
 * chapa, el documento— y que el padrón no protege con nada.
 */
export const UNICOS = {
  tractores: [["dominio", "patente", "patente"]],
  furgones: [["dominio", "patente", "patente"]],
  vehiculos: [],
  personas: [["legajo", "legajo", "numero"]],
};

/* Cómo se compara cada tipo de campo.
 *
 * LA PATENTE SE COMPARA SIN GUIONES NI ESPACIOS. Nadie la escribe igual dos
 * veces —`AB 123 CD`, `ab-123-cd`, `AB123CD`— y comparando el texto crudo el
 * duplicado no se encuentra JAMÁS, que es peor que no chequear: la pantalla
 * dice que está todo bien. Es la misma normalización que ya usa el buscador de
 * furgones.
 */
export function normalizar(como, valor) {
  const v = String(valor ?? "").trim();
  if (!v) return "";
  if (como === "patente") return v.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (como === "numero") return v.replace(/\D/g, "");
  return v.toUpperCase().replace(/\s+/g, " ");
}

/* Los choques de lo que se está por guardar contra lo que ya hay.
 *
 * `idActual` es la fila que se está editando, y se excluye: una unidad choca
 * consigo misma en cuanto se abre para corregirle otra cosa, y ahí el aviso
 * pasa a ser ruido — a la tercera vez nadie lo lee.
 *
 * Un valor VACÍO no choca con otro vacío. Hay 64 tractores sin dominio
 * cargado; si contaran como duplicados entre sí, el alta avisaría siempre y
 * empujaría a inventar una patente para poder seguir.
 */
export function conflictos(coleccion, datos, filas, idActual = null) {
  const reglas = UNICOS[coleccion] || [];
  const salida = [];

  for (const [campo, etiqueta, como] of reglas) {
    const buscado = normalizar(como, datos?.[campo]);
    if (!buscado) continue;

    const choca = (filas || []).filter(
      (f) =>
        String(f?.id ?? "") !== String(idActual ?? " ") &&
        normalizar(como, f?.[campo]) === buscado,
    );

    for (const f of choca) {
      salida.push({
        campo,
        etiqueta,
        /* El aviso muestra la forma CANÓNICA y no la tipeada. Con `ac456ef`
         * escrito y `AC456EF` guardado, repetir lo tipeado hace dudar de si
         * son la misma patente — que es justo lo que el aviso viene a
         * contestar. Lo escrito queda al lado por si el problema es el dedazo. */
        valor: buscado,
        escrito: String(datos[campo]).trim(),
        // Con QUIÉN choca. Un «ya existe» sin decir dónde obliga a ir a
        // buscarlo a mano, y lo más probable es que no se busque.
        id: f.id,
        detalle: [f.marca, f.modelo, f.empresa].filter((x) => x && x !== "0").join(" · "),
      });
    }
  }
  return salida;
}

/* El texto del aviso. Vive acá y no en el formulario para que diga lo mismo
 * desde donde se lo llame, y para que se pueda probar sin montar React. */
export function textoDe(choques) {
  if (!choques?.length) return "";
  return choques
    .map(
      (c) =>
        `La ${c.etiqueta} ${c.valor} ya está en el interno ${c.id}` +
        (c.detalle ? ` (${c.detalle})` : "") +
        ".",
    )
    .join(" ");
}
